import isEqual from 'lodash/isEqual';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import Media from 'react-media';

import { AnyProductFilterItem } from '@api/catalog';

import { FacetsFilter, PreExpandedFilters } from '@views/collections/components/AnyControlViewItems';

import Accordion from '@controls/Accordion';
import Form from '@controls/Form';
import LoadingSkeleton from '@controls/LoadingSkeleton';
import { RangeFilter } from '@controls/RangeFilter';

import Checkbox from '@components/controls/Checkbox';

import { Button, Layout, colors, scale, useTheme } from '@scripts/gds';
import { fromKopecksToRouble, sanitizeId } from '@scripts/helpers';
import { useFiltersHelper } from '@scripts/hooks';
import { useAnyHelper } from '@scripts/hooks/useAnycollectionHelper';

import ArrowIcon from '@icons/24/chevrone-right.svg';

import CheckboxSearch from './CheckboxSearch';
import RadioFilters from './RadioFilters';
import { Category, FilterResultData, ProductFilterProps } from './types';

const CategoryChildren = ({ items }: { items: Category[] }) => (
    <>
        {items.map(({ name, link }, index) => (
            <div key={link}>
                <Link legacyBehavior href={link} passHref prefetch={false}>
                    <a
                        aria-label={name}
                        css={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: scale(2),
                            color: colors.textNuanceLight,
                            fill: colors.textNuanceLight,
                            ...(index === items.length - 1 && {
                                backgroundColor: colors.backgroundMain,
                                borderRadius: scale(5, true),
                                width: 'fit-content',
                                padding: `${scale(3, true)}px ${scale(2)}px`,
                            }),
                        }}
                    >
                        {index !== items.length - 1 && (
                            <ArrowIcon
                                css={{
                                    transform: 'rotate(180deg)',
                                    fill: colors.textNuanceLight,
                                }}
                            />
                        )}
                        {name}
                    </a>
                </Link>
            </div>
        ))}
    </>
);

const FilterForm = ({
    isLoading,
    categories,
    filterFields,
    fieldsWithSearch,
    hidePriceFilter = false,
    priceRange,
    preExpandedFilters,
    onPopupClose,
    facets,
    setFacets,
    anyCollection = {
        isAnyCollection: true,
        items: [],
    },
}: ProductFilterProps) => {
    const { layout } = useTheme();
    const { query, push, pathname } = useRouter();
    const [prevFacets, setPrevFacets] = useState<{
        anyFacets: AnyProductFilterItem[];
        facets: FacetsFilter | undefined;
        preExpandedFilters: string[];
    }>();

    const rangeDefault = useMemo(() => [priceRange?.min || 0, priceRange?.max || 10000], [priceRange]);

    const isProfitable =
        query.filters && JSON.parse(String(query.filters)) && JSON.parse(String(query.filters)).is_profitable
            ? JSON.parse(String(query.filters)).is_profitable
            : null;
    const filterData = useMemo(() => {
        const singleFilters =
            anyCollection?.items && anyCollection?.items?.length > 0
                ? anyCollection?.items?.filter(item => !item.values)
                : prevFacets?.anyFacets.filter(item => !item.values);
        const multipleFilters =
            anyCollection?.items && anyCollection?.items?.length > 0
                ? anyCollection?.items?.filter(item => item.values)
                : prevFacets?.anyFacets.filter(item => item.values);

        return [...(multipleFilters || []), ...(singleFilters || [])];
    }, [filterFields, anyCollection?.items]);

    const emptyInitialValues = useMemo(() => {
        const values: { [key: string]: unknown[] } = {};
        filterData.forEach(filterField => {
            values[filterField.name] = [];
        });

        if (!hidePriceFilter) values.price = rangeDefault;

        return values;
    }, [filterData, hidePriceFilter, rangeDefault, facets]);

    const { initialValues: filterValues } = useFiltersHelper(emptyInitialValues);

    const [preExpanded, setPreExpanded] = useState<string[]>(['price']);

    const formInitialValues = useMemo(() => {
        const values: { [key: string]: unknown[] } = {};

        let newExpanded: string[] = [];

        for (const key in facets?.filters) {
            values[key] = facets?.filters[key];
        }

        preExpandedFilters?.forEach((preFilter: PreExpandedFilters) => {
            newExpanded = [
                ...newExpanded,
                ...(preFilter && preFilter.name ? [sanitizeId(preFilter.name)] : []),
                'price',
            ];
            values[preFilter.name] = [...preFilter.values.map(value => String(value.id))];
        });

        if (newExpanded.length !== facets?.filters.length) {
            newExpanded = [];
            for (const key in facets?.filters) {
                newExpanded = [...newExpanded, sanitizeId(String(key))];
            }
        }
        setPreExpanded(newExpanded);

        return values;
    }, [filterData, hidePriceFilter, rangeDefault, filterValues?.filters, facets, preExpandedFilters]);

    const stepsRangeFilter = priceRange ? fromKopecksToRouble(priceRange.max - priceRange.min) / 10 : 10;
    const isSubmitFilters = (matches: boolean) =>
        facets && facets?.filterResult?.length > 0 && matches && (preExpanded.length > 1 || preExpanded.length === 0);

    const preExpandedFiltersData: string[] = Array.from(
        new Set([
            ...(prevFacets?.preExpandedFilters.map(filter => sanitizeId(filter)) || ['price']),
            ...preExpanded.map(filter => sanitizeId(filter)),
            'price',
        ])
    );

    useEffect(() => {
        if (anyCollection.items && anyCollection.items?.length > 0)
            setPrevFacets({
                anyFacets: anyCollection.items,
                preExpandedFilters: preExpandedFilters.map((facets: PreExpandedFilters) => facets.name),
                facets: facets,
            });
    }, [anyCollection.items]);

    return (
        <>
            {!isLoading ? (
                <Form
                    initialValues={formInitialValues}
                    onSubmit={values => {
                        const keys = Object.keys(values);
                        const result: FilterResultData = {
                            filters: {},
                            category_id: '',
                            q: '',
                        };

                        try {
                            if (isProfitable && isProfitable[0]) {
                                result.filters.is_profitable = isProfitable;
                            }
                        } catch (error) {}

                        query?.q && (result['q'] = query.q.slice(1, query.q.length - 1));
                        query?.category_id &&
                            (result['category_id'] = query.category_id.slice(1, query.category_id.length - 1));

                        keys.filter(item => values[item].length > 0).map(item => (result.filters[item] = values[item]));

                        push({
                            pathname: pathname,
                            query: {
                                ...query,
                                filter: useAnyHelper(values).filterResult,
                                page: 1,
                            },
                        });

                        setFacets && setFacets(useAnyHelper(values));
                    }}
                    enableReinitialize
                >
                    {({ values, setFieldValue, resetForm }) => (
                        <>
                            <Media query={{ minWidth: layout?.breakpoints.md || 1024 }} defaultMatches>
                                {matches => (
                                    <>
                                        {matches && categories && (
                                            <div
                                                css={{
                                                    marginBottom: scale(3),
                                                    paddingTop: matches ? 0 : scale(7),
                                                }}
                                            >
                                                {filterData.map(
                                                    filterField =>
                                                        filterField.name === 'categories' && (
                                                            <CategoryChildren
                                                                key={filterField.name}
                                                                items={filterField.values}
                                                            />
                                                        )
                                                )}
                                            </div>
                                        )}

                                        <Accordion
                                            animationType="height"
                                            preExpanded={preExpandedFiltersData}
                                            key={preExpandedFiltersData.join()}
                                        >
                                            {!hidePriceFilter && (
                                                <Accordion.Item uuid="price">
                                                    <Accordion.Heading>
                                                        <Accordion.Button>Цена (₽)</Accordion.Button>
                                                    </Accordion.Heading>
                                                    <Accordion.Panel>
                                                        <Form.Field
                                                            name="price"
                                                            onLoad={e => setFieldValue('price', e)}
                                                        >
                                                            <RangeFilter
                                                                min={
                                                                    (priceRange &&
                                                                        Number(priceRange.min.toFixed(1) || 0)) ||
                                                                    0
                                                                }
                                                                max={
                                                                    (priceRange &&
                                                                        Number(priceRange.max.toFixed(1) || 0)) ||
                                                                    0
                                                                }
                                                                step={stepsRangeFilter}
                                                                value={rangeDefault}
                                                                code="price"
                                                                onChange={e =>
                                                                    rangeDefault !== e && setFieldValue('price', e)
                                                                }
                                                            />
                                                        </Form.Field>
                                                    </Accordion.Panel>
                                                </Accordion.Item>
                                            )}

                                            {filterData.map(
                                                (filterField, index) =>
                                                    filterField.name !== 'price' && (
                                                        <div key={index}>
                                                            {!filterField.values ? (
                                                                <Form.Field name={String(filterField.name)}>
                                                                    <Checkbox
                                                                        value={filterField.name}
                                                                        css={{
                                                                            marginTop: scale(3),
                                                                            display: 'block',
                                                                        }}
                                                                    >
                                                                        {filterField.name}
                                                                    </Checkbox>
                                                                </Form.Field>
                                                            ) : (
                                                                <Accordion.Item uuid={sanitizeId(filterField.name)}>
                                                                    <Accordion.Heading>
                                                                        <Accordion.Button>
                                                                            {filterField.name === 'brands'
                                                                                ? 'Бренды'
                                                                                : filterField.name === 'categories'
                                                                                  ? 'Категории'
                                                                                  : filterField.name}
                                                                        </Accordion.Button>
                                                                    </Accordion.Heading>
                                                                    <Accordion.Panel
                                                                        css={{ height: '100% !important' }}
                                                                    >
                                                                        <Form.Field name={String(filterField.name)}>
                                                                            {filterField.values ? (
                                                                                <CheckboxSearch
                                                                                    withSearch={
                                                                                        fieldsWithSearch! &&
                                                                                        fieldsWithSearch.indexOf(
                                                                                            String(filterField.name)
                                                                                        ) !== -1
                                                                                    }
                                                                                    anyCollection={{
                                                                                        isAnyCollection: true,
                                                                                        items: filterField.values,
                                                                                        isCategory:
                                                                                            filterField.name ===
                                                                                            'categories',
                                                                                    }}
                                                                                    values={values}
                                                                                />
                                                                            ) : (
                                                                                <RadioFilters
                                                                                    withSearch={
                                                                                        fieldsWithSearch! &&
                                                                                        fieldsWithSearch.indexOf(
                                                                                            String(filterField.name)
                                                                                        ) !== -1
                                                                                    }
                                                                                    anyCollection={{
                                                                                        isAnyCollection: true,
                                                                                        items: filterField.values,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Form.Field>
                                                                    </Accordion.Panel>
                                                                </Accordion.Item>
                                                            )}
                                                        </div>
                                                    )
                                            )}
                                        </Accordion>
                                        {isSubmitFilters(matches) ? (
                                            <>
                                                {!isEqual(values, emptyInitialValues) && (
                                                    <>
                                                        <Button
                                                            data-testid="button-filter"
                                                            theme="outlineGrey"
                                                            onClick={() => {
                                                                setFacets &&
                                                                    setFacets({
                                                                        filterResult: '',
                                                                        filters: {},
                                                                    });

                                                                push({
                                                                    pathname: pathname,
                                                                    query: { ...query, filter: '' },
                                                                });
                                                                setPreExpanded(['price']);

                                                                resetForm({
                                                                    values: { ...emptyInitialValues, price: [] },
                                                                });
                                                            }}
                                                            css={{
                                                                width: '100%',
                                                                marginTop: scale(2),
                                                                marginBottom: scale(2),
                                                            }}
                                                        >
                                                            Очистить все
                                                        </Button>
                                                        <Button
                                                            type="submit"
                                                            block
                                                            onClick={onPopupClose}
                                                            data-testid="button-apply"
                                                        >
                                                            Применить
                                                        </Button>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {!isEqual(values, formInitialValues) && (
                                                    <div
                                                        css={{
                                                            position: 'sticky',
                                                            width: '100%',
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            backgroundColor: 'transparent',
                                                            padding: scale(2),
                                                        }}
                                                    >
                                                        <Button
                                                            type="submit"
                                                            theme="secondary"
                                                            block
                                                            onClick={onPopupClose}
                                                            data-testid="button-apply"
                                                        >
                                                            Применить
                                                        </Button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </Media>
                        </>
                    )}
                </Form>
            ) : (
                <>
                    <Layout type="flex" gap={scale(4)} direction="column">
                        {Array.from(Array(10).keys()).map(item => (
                            <Layout.Item key={item}>
                                <LoadingSkeleton height={scale(3)} />
                            </Layout.Item>
                        ))}
                    </Layout>
                </>
            )}
        </>
    );
};

export default FilterForm;
